import { useLocation, useNavigate } from 'react-router-dom';
import { ErrorBoundary } from '@/lib/telemetry';
import { Button } from '@/components/ui/button';

/**
 * Catches a render crash from the routed page and keeps the rest of the app
 * alive around it.
 *
 * The platform had exactly one error boundary, in `main.tsx`, mounted ABOVE
 * `<BrowserRouter>`. When any component anywhere threw during render, that
 * boundary replaced the entire application — router included — with a static
 * "Something went wrong". With no router there was nothing to navigate with,
 * and because a refresh re-renders the same URL it tripped again immediately.
 * On 2026-09-19 that left the Chat arche unusable until the URL was retyped by
 * hand, and the error screen gave no hint that this was the way out.
 *
 * So this one sits INSIDE the router and inside the auth/theme/chrome providers,
 * wrapping only `<Routes>`. What that does and does not save, precisely:
 *
 *  - SURVIVES: the router, so the buttons below genuinely navigate and
 *    `resetKey={location.pathname}` clears the error the moment the user moves;
 *    and the providers (theme, auth, chrome, the global AI conversation), so
 *    recovering is not a re-login and nothing in flight is lost.
 *  - DOES NOT SURVIVE: the page — including its `<Layout>`, and therefore the
 *    header and nav. `Layout` is per-route (`App.tsx`), so it is inside this
 *    boundary, not above it. The fallback is a bare page with two buttons.
 *
 * Rendering the fallback inside `<Layout>` would put the chrome back, and is
 * deliberately not done: a crash originating in `Layout` itself would then
 * re-throw while rendering the fallback and escalate to the root boundary —
 * reproducing the exact dead end this exists to prevent. The buttons are the
 * escape hatch instead.
 *
 * The root boundary in `main.tsx` deliberately stays as the outer net — it is
 * what catches a throw in a provider above this point, where there is genuinely
 * nothing left to keep.
 */
export function RouteErrorBoundary({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();

  return (
    <ErrorBoundary
      resetKey={location.pathname}
      fallback={(reset) => (
        <div className="flex flex-col items-center justify-center px-4 py-20 text-center">
          <h1 className="text-xl font-semibold text-foreground mb-2">
            This page ran into a problem
          </h1>
          <p className="text-sm text-muted-foreground mb-6 max-w-md">
            The rest of Fias is still working — you can go back to your dashboard, or try loading
            this page again.
          </p>
          <div className="flex items-center gap-3">
            <Button onClick={reset}>Try again</Button>
            <Button
              variant="outline"
              onClick={() => {
                // Reset first: navigating to the path we are already on would
                // not change `resetKey`, so the boundary would stay tripped and
                // the button would look broken.
                reset();
                navigate('/');
              }}
            >
              Go to dashboard
            </Button>
          </div>
        </div>
      )}
    >
      {children}
    </ErrorBoundary>
  );
}
